import React, { useState } from 'react'
import {
  Nav,
  Input,
  InputGroup,
  SelectPicker,
  IconButton,
  Whisper,
  Tooltip
} from 'rsuite'
import PlusIcon from '@rsuite/icons/Plus'
import { Search } from '@rsuite/icons'
import BlockIcon from '@rsuite/icons/Block'
import CloseOutlineIcon from '@rsuite/icons/CloseOutline'
import { ReactComponent as FilterIcon } from 'assets/shared/icons/filter.svg'
import DateRangePicker, {
  DateRangeValue
} from '../dateRangePicker/DateRangePicker'
import { Button } from '../../atoms'
import './toolbar.scss'

export interface DropdownFilterOption {
  label: string
  value: string
}

export interface ToolbarFilterField {
  key: string
  placeholder: string
  options: DropdownFilterOption[]
}

interface Props {
  options: { label: string; value: string; onClick: () => void }[]
  buttonName?: string
  onButtonClick?: () => void
  total: number
  onSearchChange: (value: string) => void
  showButton?: boolean
  dropdownFilter?: {
    data: DropdownFilterOption[]
    placeholder?: string
    value?: string | null
    onChange: (value: string | null) => void
  }
  filterFields?: ToolbarFilterField[]
  // Emits the selected filter values (keyed by field key) whenever they change
  // or are cleared. Unselected fields are `null`.
  onFiltersChange?: (values: Record<string, string | null>) => void
  dateRange?: {
    value: DateRangeValue | null
    onChange: (range: DateRangeValue | null) => void
    placeholder?: string
    disableFuture?: boolean
  }
}

const Toolbar: React.FC<Props> = ({
  options,
  buttonName,
  onButtonClick,
  total,
  onSearchChange,
  showButton = true,
  dropdownFilter,
  filterFields,
  onFiltersChange,
  dateRange
}) => {
  const [active, setActive] = useState(options[0]?.value)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [filterValues, setFilterValues] = useState<
    Record<string, string | null>
  >({})

  const hasFilters = !!filterFields?.length

  const handleFilterChange = (key: string, value: string | null) => {
    const next = { ...filterValues, [key]: value }
    setFilterValues(next)
    onFiltersChange?.(next)
  }

  const handleClearFilters = () => {
    setFilterValues({})
    const cleared: Record<string, string | null> = {}
    filterFields?.forEach((field) => {
      cleared[field.key] = null
    })
    if (dateRange) {
      dateRange.onChange(null)
      cleared.fromDate = null
      cleared.toDate = null
    }
    onFiltersChange?.(cleared)
  }

  const onNavSelect = (
    activeKey: string,
    option: { label: string; value: string; onClick: () => void }
  ) => {
    setActive(activeKey)
    option.onClick()
  }

  return (
    <>
      <div className="toolbar">
        <div className="toolbar-left">
          {options.length > 0 && (
            <Nav appearance="subtle" activeKey={active}>
              {options.map((option) => (
                <Nav.Item
                  key={option.value}
                  role="tab"
                  eventKey={option.value}
                  onClick={() => onNavSelect(option.value, option)}
                >
                  {option.label}
                </Nav.Item>
              ))}
            </Nav>
          )}
          <div className="toolbar-totals">
            <span>Total: {total}</span>
          </div>
        </div>
        <div className="toolbar-right-pane">
          {dropdownFilter && (
            <SelectPicker
              data={dropdownFilter.data}
              placeholder={dropdownFilter.placeholder ?? 'Filter'}
              searchable={false}
              size="md"
              className="toolbar-dropdown"
              value={dropdownFilter.value}
              onChange={dropdownFilter.onChange}
            />
          )}

          {hasFilters && (
            <IconButton
              className={`toolbar-filter-toggle${
                isFilterOpen ? ' is-open' : ''
              }`}
              appearance="primary"
              circle
              icon={<FilterIcon />}
              aria-label="Filters"
              onClick={() => setIsFilterOpen((open) => !open)}
            />
          )}

          <div>
            <InputGroup size="md" className="toolbar-search">
              <InputGroup.Addon>
                <Search />
              </InputGroup.Addon>
              <Input
                placeholder="Search..."
                role="search"
                onChange={(value: string) => onSearchChange(value)}
              />
            </InputGroup>
          </div>

          {/* No filter panel on this page → keep the date filter in the row. */}
          {dateRange && !hasFilters && (
            <div>
              <DateRangePicker
                className="toolbar-date-range"
                block={false}
                size="md"
                placement="bottomEnd"
                value={dateRange.value}
                onChange={dateRange.onChange}
                placeholder={dateRange.placeholder ?? 'Filter by date'}
                disableFuture={dateRange.disableFuture}
              />
            </div>
          )}
          {showButton && (
            <Button
              className="toolbar-btn"
              appearance="primary"
              startIcon={<PlusIcon />}
              onClick={onButtonClick}
            >
              {buttonName}
            </Button>
          )}
        </div>
      </div>

      {hasFilters && isFilterOpen && (
        <div className="toolbar-filter-panel">
          <div className="toolbar-filter-panel__row">
            {filterFields?.map((field) => (
              <div className="toolbar-filter-panel__field" key={field.key}>
                <SelectPicker
                  data={field.options}
                  value={filterValues[field.key] ?? null}
                  onChange={(value) => handleFilterChange(field.key, value)}
                  placeholder={field.placeholder}
                  searchable={false}
                  block
                />
              </div>
            ))}

            {dateRange && (
              <div className="toolbar-filter-panel__field toolbar-filter-panel__field--date">
                <DateRangePicker
                  className="toolbar-date-range"
                  block
                  size="md"
                  placement="bottomStart"
                  value={dateRange.value}
                  onChange={dateRange.onChange}
                  placeholder={dateRange.placeholder ?? 'Filter by date'}
                  disableFuture={dateRange.disableFuture}
                />
              </div>
            )}

            <div className="toolbar-filter-panel__actions">
              <Whisper
                placement="bottom"
                trigger="hover"
                speaker={<Tooltip>Clear filters</Tooltip>}
              >
                <IconButton
                  appearance="primary"
                  circle
                  className="toolbar-filter-action"
                  icon={<BlockIcon />}
                  aria-label="Clear filters"
                  onClick={handleClearFilters}
                />
              </Whisper>
              <Whisper
                placement="bottom"
                trigger="hover"
                speaker={<Tooltip>Close</Tooltip>}
              >
                <IconButton
                  appearance="primary"
                  circle
                  className="toolbar-filter-action"
                  icon={<CloseOutlineIcon />}
                  aria-label="Close"
                  onClick={() => {
                    handleClearFilters()
                    setIsFilterOpen(false)
                  }}
                />
              </Whisper>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default Toolbar
